import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  db,
  doc,
  getDoc,
  getMentorNote,
  loadLearningPath,
} from "../../lib/firebase";
import { saveMentorNote } from "../../lib/functions";
import { resolvePathProgress } from "../../lib/learningPath";
import PathProgressView from "../paths/PathProgressView";

// ── Overlay + Panel (mirrored from LeadDrawer.js) ─────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  max-width: 100vw;
  background: var(--nd-black);
  border-left: 1px solid var(--nd-border-visible);
  z-index: 1001;
  overflow-y: auto;
  padding: 24px;
  font-family: 'Space Grotesk', sans-serif;
  color: var(--nd-text-primary);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 22px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.01em;
  margin: 0;
`;

const CloseBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  border-radius: 999px;
  color: var(--nd-text-primary);
  padding: 4px 12px;
  cursor: pointer;
`;

// ── Metric chips (mirrored from PathDashboard.js) ─────────────────────────

const MetricChip = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-secondary, #8b949e);
  background: var(--nd-surface, #161b22);
  border: 1px solid var(--nd-border);
  border-radius: 3px;
  padding: 1px 6px;
  margin-left: 4px;
`;

const MetricRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
`;

// ── Path summary ───────────────────────────────────────────────────────────

const SubTitle = styled.h3`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 20px 0 10px;
`;

const PathSummaryLine = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-primary);
  margin-bottom: 4px;
`;

// ── Note editor (mirrored from PathDashboard.js) ───────────────────────────

const NoteEditor = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--nd-border);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoteLabel = styled.label`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  display: block;
  margin-bottom: 4px;
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  min-height: 68px;
  background: var(--nd-bg, #0d1117);
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  color: var(--nd-text-primary, #c9d1d9);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  padding: 8px 10px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--nd-accent, #58a6ff);
  }
`;

const NoteFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  padding: 6px 14px;
  border-radius: 4px;
  border: 1px solid var(--nd-accent, #58a6ff);
  background: transparent;
  color: var(--nd-accent, #58a6ff);
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: rgba(88,166,255,0.08); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const NoteStatus = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: ${({ ok }) => (ok ? "var(--nd-success, #4ade80)" : "var(--nd-error, #f87171)")};
`;

// ── Helpers ────────────────────────────────────────────────────────────────

function currentLevelTitle(progress) {
  if (!progress || !Array.isArray(progress.levels) || progress.levels.length === 0) return "—";
  if (progress.complete) return progress.levels[progress.levels.length - 1].title;
  const idx = progress.levels.findIndex((_, i) => !progress.completedLevelIndexes.includes(i));
  return idx >= 0 ? progress.levels[idx].title : progress.levels[progress.levels.length - 1].title;
}

function formatCheckin(ts) {
  if (!ts) return null;
  try {
    if (ts.toDate) return ts.toDate().toLocaleDateString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
    return new Date(ts).toLocaleDateString();
  } catch (_) {
    return null;
  }
}

// ── Main component ─────────────────────────────────────────────────────────

const JuniorDrawer = ({ uid, onClose }) => {
  // Junior summary row (from mentor roster shape)
  const [row, setRow] = useState(null); // { displayName, pathTitle, pct, ... }
  // Full progress object (from resolvePathProgress) needed by PathProgressView
  const [progress, setProgress] = useState(undefined); // undefined = loading
  // Mentor note editor
  const [note, setNote] = useState(undefined); // undefined = loading, then { comment, plan }
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteStatus, setNoteStatus] = useState(null); // "saved"|"error"|null
  const noteStatusTimerRef = useRef(null);

  // Load all junior data on mount (SSR-safe: effect never runs server-side)
  useEffect(() => {
    if (typeof window === "undefined" || !db || !uid) return undefined;

    let cancelled = false;

    (async () => {
      try {
        // 1. path_assignments/{uid} → pathId + checkin
        const assignSnap = await getDoc(doc(db, "path_assignments", uid));
        const assign = assignSnap.exists() ? assignSnap.data() : null;
        const pathId = assign && assign.pathId;
        const nextCheckinAt = (assign && assign.checkin && assign.checkin.nextCheckinAt) || null;

        // 2. Parallel: user docs + path def
        const [upSnap, lpSnap, skSnap, uSnap, pathDef] = await Promise.all([
          getDoc(doc(db, "user_progress", uid)),
          getDoc(doc(db, "labProgress", uid)),
          getDoc(doc(db, "skills", uid)),
          getDoc(doc(db, "users", uid)),
          pathId ? loadLearningPath(pathId) : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const up = upSnap.exists() ? upSnap.data() : null;
        const labProgress = lpSnap.exists() ? lpSnap.data() : null;
        const skills = skSnap.exists() ? skSnap.data() : null;
        const userDoc = uSnap.exists() ? uSnap.data() : null;

        // 3. Resolve full progress for PathProgressView
        const sources = {
          labProgress,
          completedModuleIds: (up && up.completedModuleIds) || [],
          certProofs: skills ? skills.proofs : null,
        };
        const resolved = pathDef ? resolvePathProgress(pathDef, sources) : null;

        // 4. Derive display row
        const todayKey = new Date().toISOString().slice(0, 10);
        const mentorQsToday =
          (up && up.perDay && up.perDay[todayKey] && up.perDay[todayKey]["mentor.question_asked"]) || 0;
        const mentorQsTotal = (up && up.mentorQuestions) || 0;

        const displayName =
          (userDoc && (userDoc.displayName || (userDoc.email && userDoc.email.split("@")[0]))) || uid;

        setRow({
          displayName,
          pathTitle: pathDef ? pathDef.title : (pathId || ""),
          pct: resolved ? resolved.pct : 0,
          xp: (up && up.xp) || 0,
          level: (up && up.level) || 0,
          streakDays: (up && up.streakDays) || 0,
          mentorQsToday,
          mentorQsTotal,
          nextCheckinAt,
        });
        setProgress(resolved);
      } catch (_err) {
        if (!cancelled) {
          setProgress(null);
          setRow({ displayName: uid, pct: 0, xp: 0, level: 0, streakDays: 0, mentorQsToday: 0, mentorQsTotal: 0, nextCheckinAt: null });
        }
      }
    })();

    return () => { cancelled = true; };
  }, [uid]);

  // Load mentor note on mount
  useEffect(() => {
    if (typeof window === "undefined" || !uid) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const fetched = await getMentorNote(uid);
        if (!cancelled) {
          setNote({
            comment: (fetched && fetched.comment) || "",
            plan: (fetched && fetched.plan) || "",
          });
        }
      } catch (_) {
        if (!cancelled) setNote({ comment: "", plan: "" });
      }
    })();

    return () => { cancelled = true; };
  }, [uid]);

  // Cleanup transient status timer
  useEffect(
    () => () => {
      if (noteStatusTimerRef.current) clearTimeout(noteStatusTimerRef.current);
    },
    [],
  );

  const onSaveNote = async () => {
    if (!note || noteSaving) return;
    setNoteSaving(true);
    setNoteStatus(null);
    try {
      await saveMentorNote({ uid, comment: note.comment || "", plan: note.plan || "" });
      setNoteStatus("saved");
      if (noteStatusTimerRef.current) clearTimeout(noteStatusTimerRef.current);
      noteStatusTimerRef.current = setTimeout(() => {
        noteStatusTimerRef.current = null;
        setNoteStatus(null);
      }, 3000);
    } catch (_) {
      setNoteStatus("error");
    } finally {
      setNoteSaving(false);
    }
  };

  const loading = row === null;
  const checkinDate = row ? formatCheckin(row.nextCheckinAt) : null;

  return (
    <>
      <Overlay onClick={onClose} />
      <Panel role="dialog" aria-modal="true">
        <Header>
          <Title>{loading ? "Loading…" : (row ? row.displayName : uid)}</Title>
          <CloseBtn type="button" onClick={onClose}>Close</CloseBtn>
        </Header>

        {row && (
          <>
            {/* Path summary */}
            <SubTitle>Path</SubTitle>
            <PathSummaryLine>{row.pathTitle || "—"}</PathSummaryLine>
            <PathSummaryLine>
              {row.pct}% · {currentLevelTitle(progress)}
            </PathSummaryLine>

            {/* Metric chips */}
            <MetricRow>
              <MetricChip>{row.xp.toLocaleString()} xp</MetricChip>
              <MetricChip>lvl {row.level}</MetricChip>
              <MetricChip>
                {row.streakDays > 0 ? `${row.streakDays}d streak` : "no streak"}
              </MetricChip>
              <MetricChip>
                {row.mentorQsToday > 0 ? `${row.mentorQsToday} qs today` : "0 qs today"}
              </MetricChip>
              {row.mentorQsTotal > 0 && (
                <MetricChip>{row.mentorQsTotal} qs total</MetricChip>
              )}
            </MetricRow>

            {/* Next check-in */}
            {checkinDate && (
              <PathSummaryLine style={{ marginBottom: 20, fontSize: 12, fontFamily: "'Space Mono', monospace", color: "var(--nd-text-disabled)" }}>
                Next check-in: {checkinDate}
              </PathSummaryLine>
            )}
          </>
        )}

        {/* PathProgressView — only when progress is resolved */}
        {progress !== undefined && progress !== null && row && (
          <>
            <SubTitle>Progress</SubTitle>
            <PathProgressView pathTitle={row.pathTitle} progress={progress} />
          </>
        )}

        {/* Mentor note editor */}
        <NoteEditor>
          <SubTitle style={{ margin: "0 0 4px" }}>Mentor Notes</SubTitle>
          <div>
            <NoteLabel htmlFor={`drawer-note-comment-${uid}`}>Comment</NoteLabel>
            <NoteTextarea
              id={`drawer-note-comment-${uid}`}
              value={(note && note.comment) || ""}
              placeholder={note === undefined ? "Loading…" : "Notes for this junior…"}
              disabled={note === undefined || noteSaving}
              onChange={(e) =>
                setNote((prev) => ({ ...(prev || {}), comment: e.target.value }))
              }
            />
          </div>
          <div>
            <NoteLabel htmlFor={`drawer-note-plan-${uid}`}>Plan</NoteLabel>
            <NoteTextarea
              id={`drawer-note-plan-${uid}`}
              value={(note && note.plan) || ""}
              placeholder={note === undefined ? "Loading…" : "Development plan…"}
              disabled={note === undefined || noteSaving}
              onChange={(e) =>
                setNote((prev) => ({ ...(prev || {}), plan: e.target.value }))
              }
            />
          </div>
          <NoteFooter>
            <SaveBtn
              type="button"
              disabled={note === undefined || noteSaving}
              onClick={onSaveNote}
            >
              {noteSaving ? "Saving…" : "Save"}
            </SaveBtn>
            {noteStatus === "saved" && <NoteStatus ok>Saved</NoteStatus>}
            {noteStatus === "error" && <NoteStatus>Error — try again</NoteStatus>}
          </NoteFooter>
        </NoteEditor>
      </Panel>
    </>
  );
};

export default JuniorDrawer;
